import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import ContentPageLayout from "../components/ContentPageLayout";
import BoardPreview from "../components/BoardPreview";
import JsonLd from "../components/JsonLd";
import { STORYBOARD_TEMPLATE_PAGES } from "../content/storyboardTemplates";
import { GUIDES } from "../content/guides";
import { DEFAULT_STORYBOARD_TEMPLATE } from "../constants/storyboardTemplates";
import { trackEvent } from "../utils/analytics";

const FEATURES = [
  {
    icon: "aspect_ratio",
    title: "Frames locked to your aspect ratio",
    text: "Pick 16:9, 2.39:1, 1.85:1, 4:3, 1:1, 4:5, or 9:16 and every frame on the board takes that shape — change it once and the whole board follows.",
  },
  {
    icon: "crop",
    title: "Letterbox bars and crop guides",
    text: "Show 2.39:1 letterbox bars inside 16:9 frames, or shade what a 9:16 vertical crop will lose, so one shoot can serve YouTube and Reels.",
  },
  {
    icon: "edit_note",
    title: "Shot notes under every frame",
    text: "Shot type, camera move, duration, action, and dialogue — typed straight onto the board and printed with it. Shots number across pages.",
  },
  {
    icon: "draw",
    title: "Sketch, paste, or drop images",
    text: "Drop a phone photo of a thumbnail sketch, a reference still, or a finished frame onto any panel and drag it to reframe.",
  },
  {
    icon: "picture_as_pdf",
    title: "Print-ready PDF export",
    text: "Export a single board or the whole storyboard as one PDF, or any page as a PNG to drop into a deck or a pitch.",
  },
  {
    icon: "lock_open",
    title: "Free, no account",
    text: "Striply runs in your browser and saves your boards there automatically. No sign-up, no watermark.",
  },
];

const FAQ = [
  {
    q: "Is this storyboard maker really free?",
    a: "Yes. Striply is free to use with no account and no watermark on exports.",
  },
  {
    q: "Do I need to be able to draw?",
    a: "No. Stick figures and arrows are fine — what matters is that each frame shows the framing clearly and the notes say what happens. You can also drop in photos, reference stills, or sketches made on paper.",
  },
  {
    q: "Which aspect ratio should I storyboard in?",
    a: "Board in the ratio you will deliver: 16:9 for YouTube and most video, 2.39:1 or 1.85:1 for widescreen film, and 9:16 for Reels, TikTok, and Shorts. If you are delivering more than one, board in the widest and add a crop guide for the others.",
  },
  {
    q: "Can I print my storyboard?",
    a: "Yes. Export the whole storyboard as a PDF on US Letter, A4, or tabloid paper, or download any template as a blank printable PDF to sketch on by hand.",
  },
  {
    q: "Where are my storyboards saved?",
    a: "In your browser's local storage on the device you are using, saved automatically as you work. Export a PDF or a layout file to keep a copy elsewhere.",
  },
];

const StoryboardLandingPage = () => {
  useEffect(() => {
    trackEvent("storyboard_landing_viewed");
  }, []);

  const storyboardGuides = GUIDES.filter((g) => g.cluster === "storyboards");

  return (
    <ContentPageLayout>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Striply Storyboard Maker",
          url: "https://striply.xyz/storyboard",
          applicationCategory: "DesignApplication",
          operatingSystem: "Any (web browser)",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          description:
            "A free online storyboard maker with aspect-locked frames, letterbox mattes and crop guides, shot notes, and PDF export.",
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }}
      />

      <main style={styles.main}>
        <section style={styles.hero}>
          <div style={styles.heroText}>
            <h1 style={styles.title}>Free Online Storyboard Maker</h1>
            <p style={styles.lede}>
              Plan every shot before you film. Striply gives you frames locked to
              your aspect ratio, letterbox and vertical crop guides, and room for
              shot notes under every frame — then exports a clean PDF your crew
              or client can read.
            </p>
            <div style={styles.ctaRow}>
              <Link
                to="/storyboard/editor"
                style={styles.primaryCta}
                onClick={() => trackEvent("storyboard_cta", { from: "landing_hero" })}
              >
                Start a storyboard
              </Link>
              <Link to="/storyboard-templates" style={styles.secondaryCta}>Browse templates</Link>
            </div>
          </div>
          <div style={styles.heroPreview}>
            <BoardPreview
              seed={DEFAULT_STORYBOARD_TEMPLATE.seed}
              title="A blank 16:9 storyboard page with six frames and shot notes"
              style={styles.previewPaper}
            />
          </div>
        </section>

        <section>
          <h2 style={styles.sectionTitle}>Built for how boards are really used</h2>
          <div style={styles.featureGrid}>
            {FEATURES.map((f) => (
              <div key={f.title} style={styles.featureCard}>
                <span className="material-icons" aria-hidden="true" style={styles.featureIcon}>{f.icon}</span>
                <h3 style={styles.featureTitle}>{f.title}</h3>
                <p style={styles.featureText}>{f.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 style={styles.sectionTitle}>How to make a storyboard in Striply</h2>
          <ol style={styles.steps}>
            <li>
              <strong>Choose a format.</strong> Start from a{" "}
              <Link to="/storyboard-templates" style={styles.inlineLink}>storyboard template</Link>,
              or pick a frame ratio and a board layout in the toolbar.
            </li>
            <li>
              <strong>Fill the frames.</strong> Drop in sketches or photos, and use
              speech bubbles for on-screen text where it helps.
            </li>
            <li>
              <strong>Write the notes.</strong> Set each shot's type, camera move,
              and duration, then describe the action and dialogue. Not sure of the
              terms? Read our{" "}
              <Link to="/guides/camera-shot-types" style={styles.inlineLink}>guide to camera shot types</Link>.
            </li>
            <li>
              <strong>Share it.</strong> Add pages as the sequence grows — shot
              numbers carry on across pages — and export the whole storyboard as
              one PDF.
            </li>
          </ol>
        </section>

        <section>
          <h2 style={styles.sectionTitle}>Storyboard templates</h2>
          <div style={styles.templateGrid}>
            {STORYBOARD_TEMPLATE_PAGES.map((t) => (
              <Link key={t.slug} to={`/storyboard-templates/${t.slug}`} style={styles.templateCard}>
                <BoardPreview seed={t.seed} title={`${t.title} preview`} />
                <span style={styles.templateName}>{t.title}</span>
                <span style={styles.templateUse}>{t.useCase}</span>
              </Link>
            ))}
          </div>
        </section>

        <section>
          <h2 style={styles.sectionTitle}>Learn the craft</h2>
          <ul style={styles.guideList}>
            {storyboardGuides.map((g) => (
              <li key={g.slug} style={styles.guideItem}>
                <Link to={`/guides/${g.slug}`} style={styles.guideLink}>{g.title}</Link>
                <span style={styles.guideSummary}> — {g.summary}</span>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 style={styles.sectionTitle}>Frequently asked questions</h2>
          {FAQ.map(({ q, a }) => (
            <div key={q} style={styles.faqItem}>
              <h3 style={styles.faqQ}>{q}</h3>
              <p style={styles.faqA}>{a}</p>
            </div>
          ))}
        </section>

        <section style={styles.closing}>
          <p style={styles.closingText}>
            Making a comic instead? Striply started as a{" "}
            <Link to="/editor" style={styles.inlineLink}>comic page layout editor</Link>, with
            free-form panels, lettering, and sound effects.
          </p>
          <Link
            to="/storyboard/editor"
            style={styles.primaryCta}
            onClick={() => trackEvent("storyboard_cta", { from: "landing_footer" })}
          >
            Start a storyboard
          </Link>
        </section>
      </main>
    </ContentPageLayout>
  );
};

const styles = {
  main: {
    flex: 1,
    width: "100%",
    maxWidth: 1040,
    margin: "0 auto",
    padding: "40px 16px 56px",
    boxSizing: "border-box",
  },
  hero: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 36,
    marginBottom: 56,
  },
  heroText: { flex: "1 1 340px", minWidth: 0 },
  heroPreview: { flex: "1 1 380px", minWidth: 0 },
  previewPaper: {
    border: "2px solid #1a1a1a",
    boxShadow: "6px 6px 0 rgba(0, 0, 0, 0.18)",
  },
  title: {
    fontSize: "clamp(32px, 6vw, 46px)",
    fontWeight: 700,
    lineHeight: 1.15,
    margin: "0 0 16px",
    color: "#1a1a1a",
  },
  lede: { fontSize: 18, color: "#444", margin: "0 0 24px" },
  ctaRow: { display: "flex", flexWrap: "wrap", gap: 12 },
  primaryCta: {
    display: "inline-block",
    padding: "14px 28px",
    background: "#1a1a1a",
    color: "#f5f0e8",
    fontSize: 15,
    fontWeight: 700,
    textDecoration: "none",
    textTransform: "uppercase",
    letterSpacing: 1,
    border: "2px solid #1a1a1a",
  },
  secondaryCta: {
    display: "inline-block",
    padding: "14px 28px",
    background: "transparent",
    color: "#1a1a1a",
    fontSize: 15,
    fontWeight: 700,
    textDecoration: "none",
    textTransform: "uppercase",
    letterSpacing: 1,
    border: "2px solid #1a1a1a",
  },
  sectionTitle: {
    fontSize: 26,
    fontWeight: 700,
    margin: "48px 0 18px",
    color: "#1a1a1a",
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: 16,
  },
  featureCard: {
    border: "2px solid #1a1a1a",
    background: "#fffdf6",
    padding: "18px 18px 14px",
  },
  featureIcon: { fontSize: 26, color: "#00a4e4" },
  featureTitle: { fontSize: 17, fontWeight: 700, margin: "8px 0 6px", lineHeight: 1.3 },
  featureText: { fontSize: 15, color: "#555", margin: 0, lineHeight: 1.6 },
  steps: { fontSize: 16, color: "#333", lineHeight: 1.7, paddingLeft: 22, margin: 0 },
  inlineLink: { color: "#00a4e4", textDecoration: "none" },
  templateGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: 18,
  },
  templateCard: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    textDecoration: "none",
    color: "#1a1a1a",
    border: "2px solid #1a1a1a",
    background: "#fffdf6",
    padding: 12,
  },
  templateName: { fontSize: 16, fontWeight: 700, lineHeight: 1.3 },
  templateUse: { fontSize: 14, color: "#666", lineHeight: 1.5 },
  guideList: { listStyle: "none", margin: 0, padding: 0 },
  guideItem: { marginBottom: 12, fontSize: 16, lineHeight: 1.5 },
  guideLink: { color: "#00a4e4", textDecoration: "none", fontWeight: 700 },
  guideSummary: { color: "#666", fontSize: 15 },
  faqItem: { borderTop: "1px solid rgba(26, 26, 26, 0.2)", padding: "14px 0" },
  faqQ: { fontSize: 17, fontWeight: 700, margin: "0 0 6px" },
  faqA: { fontSize: 15, color: "#444", margin: 0, lineHeight: 1.65 },
  closing: {
    textAlign: "center",
    marginTop: 56,
    paddingTop: 32,
    borderTop: "2px solid #1a1a1a",
  },
  closingText: { fontSize: 16, color: "#555", margin: "0 0 20px" },
};

export default StoryboardLandingPage;
